import { z } from 'zod';

import { BoListQueryBase } from '@Common/types/bo/bo_list_query';
import { buildZodBoListQueryParsed } from '@Common/types/bo/utils/build_zod_bo_list_query';
import { ZodId, ZodStringIDPathParam } from '@Common/types/request';
import { createZodArrayable } from '@Common/types/zod/zod-arrayable';
import { ZodBrandedDateString } from '@Common/types/zod/zod-branded-date';
import { endpoint, GetEndpointType } from '@Common/types/zod/zod-endpoint';
import { ZodTrimmedString } from '@Common/types/zod/zod-trimmed-string';

const ZodGetMigratedCampaignOneResponse = z.object({
  id: ZodTrimmedString,
  b2b_company_id: ZodId.nullable(),
  company_name: ZodTrimmedString.nullable(),
  created_at: ZodBrandedDateString.nullable(),
  fiscal_period_start: ZodTrimmedString.nullable(),
  fiscal_period_end: ZodTrimmedString.nullable(),
  processing_start_date: ZodTrimmedString.nullable(),
  processing_end_date: ZodTrimmedString.nullable(),
  type: ZodTrimmedString.nullable(),
  subtype: ZodTrimmedString.nullable(),
  status: ZodTrimmedString.nullable(),
  amount_brut: z.number().int().nullable(),
  amount_net: z.number().int().nullable(),
  source_instrument: ZodTrimmedString.nullable(),
  target_instrument: ZodTrimmedString.nullable(),
  source_plan_piloted: ZodTrimmedString.nullable(),
  source_plan_libre: ZodTrimmedString.nullable(),
  target_plan_piloted: ZodTrimmedString.nullable(),
  target_plan_libre: ZodTrimmedString.nullable(),
  plan_types: ZodTrimmedString.array().nullable(),
});
export type GetMigratedCampaignOneResponse = z.infer<typeof ZodGetMigratedCampaignOneResponse>;

const ZodGetMigratedCampaignsResponse = z.array(ZodGetMigratedCampaignOneResponse);
export type GetMigratedCampaignsResponse = z.TypeOf<typeof ZodGetMigratedCampaignsResponse>;

export const getMigratedCampaignsSortColumns: (keyof GetMigratedCampaignsResponse[number])[] = [
  'id',
  'b2b_company_id',
  'created_at',
  'type',
  'subtype',
  'status',
];

const ZodFilters = z
  .object({
    id: createZodArrayable(ZodTrimmedString),
    b2b_company_id: createZodArrayable(ZodId),
    status: createZodArrayable(ZodTrimmedString),
    subtype: createZodArrayable(ZodTrimmedString),
    from_created_at: ZodTrimmedString,
    to_created_at: ZodTrimmedString,
  })
  .partial();

const ZodGetMigratedCampaignsQuery = buildZodBoListQueryParsed(
  ZodFilters,
  getMigratedCampaignsSortColumns,
  { range: [0, 100] },
);

export type GetMigratedCampaignsQuery = BoListQueryBase<
  z.TypeOf<typeof ZodFilters>,
  (typeof getMigratedCampaignsSortColumns)[number]
>;

export const getMigratedCampaignsEndpoint = endpoint({
  method: 'GET',
  queryParams: ZodGetMigratedCampaignsQuery,
  responseBody: ZodGetMigratedCampaignsResponse,
});
export type GetMigratedCampaignsEndpoint = GetEndpointType<typeof getMigratedCampaignsEndpoint>;

export const getMigratedCampaignsIdEndpoint = endpoint({
  method: 'GET',
  pathParams: ZodStringIDPathParam,
  responseBody: ZodGetMigratedCampaignOneResponse,
});
export type GetMigratedCampaignsIdEndpoint = GetEndpointType<typeof getMigratedCampaignsIdEndpoint>;
